import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function MvirsLoginScreen() {
  const { webUser, mvirsLogin, getMvirsUsers, logout } = useAuth();
  const [users, setUsers]           = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [password, setPassword]     = useState('');
  const [error, setError]           = useState('');
  const [loading, setLoading]       = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const navigate = useNavigate();

  // Load MVIRS users for dropdown
  useEffect(() => {
    getMvirsUsers().then((list) => {
      setUsers(list);
      // Pre-select last used user from localStorage
      const lastUser = localStorage.getItem('mvirs_last_user');
      if (lastUser && list.find(u => u.userId === lastUser)) {
        setSelectedUser(lastUser);
      } else if (list.length > 0) {
        setSelectedUser(list[0].userId);
      }
      setLoadingUsers(false);
    });
  }, [getMvirsUsers]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await mvirsLogin(selectedUser, password);
      localStorage.setItem('mvirs_last_user', selectedUser);
      navigate('/dashboard');
    } catch (err) {
      setError('Invalid username or password.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div style={s.container}>
      <div style={s.card}>
        {/* Station badge */}
        <div style={s.stationBadge}>
          <div style={s.stationLabel}>Signed into</div>
          <div style={s.stationName}>{webUser?.shopName || webUser?.shopId}</div>
        </div>

        <h2 style={s.title}>MVIRS Sign In</h2>
        <p style={s.subtitle}>Select your user and enter your MVIRS password</p>

        {error && <div style={s.error}>{error}</div>}

        <form onSubmit={handleLogin}>
          {/* MVIRS Username Dropdown */}
          <div style={s.field}>
            <label style={s.label}>MVIRS Username</label>
            {loadingUsers ? (
              <div style={s.loadingInput}>Loading users...</div>
            ) : users.length === 0 ? (
              <div style={s.loadingInput}>Could not load users. Check connection.</div>
            ) : (
              <select
                style={s.select}
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                required
              >
                {users.map((u) => (
                  <option key={u.userId} value={u.userId}>
                    {u.fullName ? `${u.fullName} (${u.userId})` : u.userId}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* MVIRS Password */}
          <div style={s.field}>
            <label style={s.label}>MVIRS Password</label>
            <input
              style={s.input}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your MVIRS password"
              required
            />
          </div>

          <button
            style={loading || loadingUsers ? s.buttonDisabled : s.button}
            type="submit"
            disabled={loading || loadingUsers || users.length === 0}
          >
            {loading ? 'Signing In...' : 'Sign In'}
          </button>
        </form>

        <div style={s.divider} />

        <button style={s.signOutBtn} onClick={handleSignOut}>
          Sign out of {webUser?.username}
        </button>
      </div>
    </div>
  );
}

const s = {
  container:    { minHeight: '100vh', backgroundColor: '#f0f2f5', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  card:         { backgroundColor: '#fff', padding: '40px', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', width: '100%', maxWidth: '400px' },
  stationBadge: { backgroundColor: '#f0f7ff', border: '1px solid #cce0ff', borderRadius: 10, padding: '12px 16px', marginBottom: 24, textAlign: 'center' },
  stationLabel: { fontSize: 12, color: '#666', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 },
  stationName:  { fontSize: 16, color: '#1a1a2e', fontWeight: 700 },
  title:        { fontSize: '24px', fontWeight: 'bold', color: '#1a1a2e', margin: '0 0 6px 0', textAlign: 'center' },
  subtitle:     { color: '#666', fontSize: '14px', marginBottom: '24px', textAlign: 'center' },
  field:        { marginBottom: '20px' },
  label:        { display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#333' },
  input:        { width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '16px', boxSizing: 'border-box', outline: 'none' },
  select:       { width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '16px', boxSizing: 'border-box', outline: 'none', backgroundColor: '#fff', cursor: 'pointer' },
  loadingInput: { padding: '12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', color: '#999', backgroundColor: '#fafafa' },
  button:       { width: '100%', padding: '14px', backgroundColor: '#1a1a2e', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: 'pointer', marginTop: '8px' },
  buttonDisabled: { width: '100%', padding: '14px', backgroundColor: '#999', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: 'not-allowed', marginTop: '8px' },
  error:        { backgroundColor: '#ffe0e0', color: '#cc0000', padding: '12px', borderRadius: '8px', marginBottom: '20px', fontSize: '14px', textAlign: 'center' },
  divider:      { borderTop: '1px solid #eee', margin: '24px 0' },
  signOutBtn:   { width: '100%', padding: '12px', backgroundColor: 'transparent', color: '#666', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' },
};